import { motion } from "framer-motion";
import { Star, Wallet, MapPin } from "lucide-react";
import { useLang } from "@/App";
import { copy } from "@/i18n";

const About = () => {
    const { lang } = useLang();
    const c = copy[lang].about;

    return (
        <section id="about" data-testid="about-section" className="relative py-20 lg:py-32 overflow-hidden">
            <div className="absolute top-1/3 -right-40 w-[480px] h-[480px] rounded-full bg-crimson/10 blur-[120px] pointer-events-none" />
            <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8 }}
                >
                    <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-crimson">
                        {c.label}
                    </span>
                    <h2 className="mt-4 font-display font-extrabold uppercase tracking-tight leading-[0.95] text-2xl sm:text-4xl lg:text-5xl">
                        {c.titleA}
                        <br />
                        <span className="text-outline">{c.titleB}</span>
                    </h2>
                    <p className="mt-6 text-base sm:text-lg text-neutral-300 leading-relaxed">{c.p1}</p>
                    <motion.figure
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="mt-8"
                    >
                        <div className="rounded-2xl overflow-hidden border border-brass/25 glow-red">
                            <div className="grid grid-cols-2">
                                <img
                                    src="/media/barbara.webp"
                                    alt="Barbara, titolare di The Market Lugano"
                                    className="w-full h-full object-cover block"
                                    loading="lazy"
                                />
                                <img
                                    src="/media/marco.webp"
                                    alt="Marco, titolare di The Market Lugano"
                                    className="w-full h-full object-cover block"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                        <figcaption className="mt-3 flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.25em] text-brass">
                            <span className="h-px w-8 bg-brass/50" />
                            Barbara &amp; Marco
                        </figcaption>
                    </motion.figure>
                    <p className="mt-8 text-base text-neutral-400 leading-relaxed">{c.p2}</p>
                    <p className="mt-6 font-serif italic text-lg sm:text-xl text-brass/90 leading-relaxed">{c.quote}</p>

                    <div className="mt-10 grid grid-cols-3 gap-4">
                        {[
                            { icon: Star, value: "4.7 / 172", label: c.stat1 },
                            { icon: Wallet, value: "CHF 20–30", label: c.stat2 },
                            { icon: MapPin, value: "Via Lucchini 12", label: c.stat3 },
                        ].map((s, i) => (
                            <div
                                key={i}
                                data-testid={`about-stat-${i}`}
                                className="rounded-2xl border border-white/10 bg-coal/60 p-4"
                            >
                                <s.icon size={16} className="text-brass mb-2" />
                                <div className="font-heading font-bold text-sm sm:text-base">{s.value}</div>
                                <div className="text-[11px] text-neutral-500 mt-1 leading-snug">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9 }}
                    className="relative"
                >
                    <div className="rounded-2xl overflow-hidden border border-white/10 glow-red">
                        <img
                            src="/media/vetrina.png"
                            alt="The Market Lugano — dove c'è tagliere deve esserci bicchiere"
                            className="w-full h-[420px] sm:h-[560px] object-cover"
                            loading="lazy"
                        />
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default About;
